import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "sonner";
import { X, CalendarIcon, Users, Clock, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "917004810637";
const DAY_OUTING_PRICE_PER_PERSON = 249;
const GST_PERCENT = 5;

const ARRIVAL_SLOTS = ["09:00 AM", "10:00 AM", "11:00 AM", "12:00 PM", "01:00 PM", "02:00 PM", "03:00 PM"];

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function DayOutingModal({ open, onClose }) {
  const nav = useNavigate();
  const dialogRef = useRef(null);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", guests: 2, arrival_time: "10:00 AM" });
  const [date, setDate] = useState(undefined);
  const [loading, setLoading] = useState(false);

  const guests = Math.max(1, Number(form.guests) || 1);
  const subtotal = DAY_OUTING_PRICE_PER_PERSON * guests;
  const gst = Math.round((subtotal * GST_PERCENT) / 100);
  const grandTotal = subtotal + gst;

  // Esc + body scroll lock
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const validate = () => {
    if (!form.full_name.trim()) return "Please enter your full name.";
    if (!form.email.trim()) return "Please enter your email.";
    if (!form.phone.trim()) return "Please enter your phone number.";
    if (!date) return "Please pick a date.";
    if (guests < 1) return "At least 1 guest required.";
    if (!form.arrival_time) return "Please pick an arrival time.";
    return null;
  };

  const createOuting = async () => {
    const err = validate();
    if (err) { toast.error(err); return null; }
    const payload = {
      full_name: form.full_name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      date: format(date, "yyyy-MM-dd"),
      guests,
      arrival_time: form.arrival_time,
    };
    const { data } = await axios.post(`${API}/day-outings`, payload);
    return data;
  };

  const chatOnWhatsApp = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setLoading(true);
    try {
      const outing = await createOuting();
      if (!outing) return;
      const dateStr = format(date, "d MMM yyyy");
      const msg =
        `Hi Tikratoli! I'd like to book a 1-Day Farm Outing.%0A%0A` +
        `Name: ${form.full_name}%0A` +
        `Date: ${dateStr}%0A` +
        `Guests: ${guests}%0A` +
        `Arrival: ${form.arrival_time}%0A` +
        `Phone: ${form.phone}%0A` +
        `Booking ID: ${outing.id.substring(0,8)}`;
      window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${msg}`, "_blank");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create outing. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const payNow = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    if (!(await loadRazorpay())) { toast.error("Could not load payment gateway."); return; }
    setLoading(true);
    try {
      const outing = await createOuting();
      if (!outing) return;
      const { data } = await axios.post(`${API}/day-outings/create-order`, { outing_id: outing.id });
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount_paise,
        currency: "INR",
        name: "Tikratoli Farm Resorts",
        description: `Day Outing · ${guests} guest${guests > 1 ? "s" : ""} · ${format(date, "d MMM yyyy")}`,
        order_id: data.order_id,
        prefill: data.guest,
        theme: { color: "#D97645" },
        handler: async (resp) => {
          try {
            await axios.post(`${API}/day-outings/verify`, {
              outing_id: outing.id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment received — your day outing is confirmed.");
            onClose();
            nav(`/day-outing/confirmed/${outing.id}`);
          } catch {
            toast.error("Payment could not be verified. Please contact us.");
          }
        },
        modal: { ondismiss: () => toast("Payment cancelled.") },
      });
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start payment.");
    } finally {
      setLoading(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (dialogRef.current && !dialogRef.current.contains(e.target)) onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Book your 1-Day Farm Outing"
      data-testid="day-outing-modal"
      onMouseDown={handleBackdropClick}
      className="fixed inset-0 z-50 flex items-center justify-center p-3 md:p-6 bg-[#1C1913]/85 backdrop-blur-sm overflow-y-auto"
    >
      <div
        ref={dialogRef}
        className="relative w-full max-w-2xl bg-[#27221C] border border-[#3A3229] shadow-2xl shadow-black/60 my-6"
      >
        {/* Close */}
        <button
          type="button"
          data-testid="day-outing-close"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center text-[#B8A891] hover:text-[#F2E7D0] hover:bg-[#3A3229] transition"
        >
          <X size={18} />
        </button>

        <div className="px-6 md:px-10 pt-10 pb-8 max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="font-mono-label text-[11px] tracking-[0.28em] text-[#D97645]">1-DAY FARM OUTING</div>
          <h2 className="mt-3 font-display text-3xl md:text-4xl leading-[1.1] text-[#F2E7D0]">
            Book your <em className="italic text-[#D97645]">farm outing</em>
          </h2>
          <div className="mt-3 font-mono-label text-xs text-[#B8A891]">
            ₹ {DAY_OUTING_PRICE_PER_PERSON} + GST · per person
          </div>

          {/* Club Membership perks */}
          <div className="mt-5 border border-[#D97645]/40 bg-[#1C1913]/70 p-5">
            <div className="flex items-center gap-2 font-mono-label text-[11px] tracking-[0.22em] text-[#D97645] mb-2">
              <Sparkles size={14} /> BECOME A TIKRATOLI CLUB MEMBER FOR A DAY
            </div>
            <ul className="space-y-1 text-sm text-[#F2E7D0]">
              <li>• 15% off Food &amp; Beverages</li>
              <li>• 10% off Jeep Rides &amp; ATV</li>
              <li>• 10% off Indoor Games</li>
            </ul>
            <div className="mt-2 font-mono-label text-[10px] tracking-[0.2em] text-[#B8A891]">
              SHOW YOUR BOOKING ID AT RECEPTION TO AVAIL
            </div>
          </div>

          {/* Form */}
          <form onSubmit={(e) => e.preventDefault()} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="day-outing-form">
            <div className="md:col-span-2">
              <Label className="font-mono-label text-[#B8A891]">Full name</Label>
              <Input data-testid="do-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="mt-2 bg-transparent border-white/20 rounded-none h-11" />
            </div>
            <div>
              <Label className="font-mono-label text-[#B8A891]">Email</Label>
              <Input data-testid="do-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 bg-transparent border-white/20 rounded-none h-11" />
            </div>
            <div>
              <Label className="font-mono-label text-[#B8A891]">Phone</Label>
              <Input data-testid="do-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2 bg-transparent border-white/20 rounded-none h-11" />
            </div>
            <div>
              <Label className="font-mono-label text-[#B8A891] flex items-center gap-2"><CalendarIcon size={13} /> Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" data-testid="do-date-btn" className="mt-2 w-full h-11 bg-transparent border border-white/20 px-3 flex items-center justify-between text-left text-[#F2E7D0] text-sm">
                    <span>{date ? format(date, "d MMM yyyy") : "Pick a date"}</span>
                    <CalendarIcon size={14} className="text-[#B8A891]" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="bg-[#27221C] border border-white/20 rounded-none" align="start">
                  <Calendar mode="single" selected={date} onSelect={setDate} disabled={{ before: new Date() }} />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="font-mono-label text-[#B8A891] flex items-center gap-2"><Users size={13} /> Guests</Label>
              <Input data-testid="do-guests" type="number" min={1} max={200} value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })} className="mt-2 bg-transparent border-white/20 rounded-none h-11" />
            </div>
            <div>
              <Label className="font-mono-label text-[#B8A891] flex items-center gap-2"><Clock size={13} /> Arrival</Label>
              <Select value={form.arrival_time} onValueChange={(v) => setForm({ ...form, arrival_time: v })}>
                <SelectTrigger data-testid="do-arrival" className="mt-2 bg-transparent border-white/20 rounded-none h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#27221C] border-white/20 rounded-none">
                  {ARRIVAL_SLOTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </form>

          {/* Price summary */}
          <div className="mt-5 border border-white/10 p-4 text-sm space-y-1" data-testid="do-price-summary">
            <div className="flex justify-between text-[#B8A891]"><span>{guests} × ₹ {DAY_OUTING_PRICE_PER_PERSON}</span><span className="text-[#F2E7D0]">₹ {subtotal.toLocaleString("en-IN")}</span></div>
            <div className="flex justify-between text-[#B8A891]"><span>GST ({GST_PERCENT}%)</span><span className="text-[#F2E7D0]">₹ {gst.toLocaleString("en-IN")}</span></div>
            <div className="flex justify-between border-t border-white/10 pt-2 mt-1">
              <span className="font-mono-label text-[#B8A891]">Total payable</span>
              <span className="font-display text-xl text-[#D97645]">₹ {grandTotal.toLocaleString("en-IN")}</span>
            </div>
          </div>

          {/* CTAs */}
          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
            <Button
              type="button"
              data-testid="do-whatsapp-btn"
              disabled={loading}
              onClick={chatOnWhatsApp}
              variant="outline"
              className="rounded-none bg-transparent border border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-[#1C1913] font-mono-label h-12 px-6"
            >
              Confirm &amp; Chat on WhatsApp
            </Button>
            <Button
              type="button"
              data-testid="do-pay-btn"
              disabled={loading}
              onClick={payNow}
              className="rounded-none bg-[#D97645] hover:bg-[#C0602F] text-[#F2E7D0] font-mono-label h-12 px-6"
            >
              Pay &amp; Confirm · ₹ {grandTotal.toLocaleString("en-IN")}
            </Button>
          </div>

          {/* Tagline + T&C */}
          <div className="mt-6 pt-5 border-t border-white/10">
            <p className="text-center font-display text-lg md:text-xl font-bold tracking-[0.3em] text-[#F2E7D0]">
              RELAX. DINE. PLAY. EXPLORE.
            </p>
            <p className="mt-4 text-[11px] text-[#B8A891] leading-relaxed">
              <span className="font-mono-label tracking-[0.2em] text-[#D97645]">T&amp;C APPLY · </span>
              Discounts are subject to facility availability and applicable resort policies. Discounts cannot be clubbed with any other ongoing offer or promotion.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
